import { Router } from '@angular/router';
import { AfterViewInit, Component, DestroyRef, inject, Input, OnChanges, OnInit, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { MapInfoWindow, GoogleMap, MapAdvancedMarker } from '@angular/google-maps';
import { timer } from 'rxjs';
import { first } from 'rxjs/operators';
import { Device } from '../../models/device';
import { GoogleMarker, InfoContentData } from '../../models/googleMarker';
import { MainSubscriptionsService } from '../../services/main-subscriptions/main-subscriptions.service';
import { PassDataService } from '../../services/pass-data/pass-data.service';
import { Location } from '../../models/location';
import { DomainProperty } from '../../models/domainProperty';
import { Icon } from '../../models/icon';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DeviceType } from '../../models/deviceType';

@Component({
    selector: 'urban-traffic-maps-widget',
    templateUrl: './traffic-maps-widget.component.html',
    styleUrls: ['./traffic-maps-widget.component.scss'],
    standalone: false
})
export class TrafficMapsWidgetComponent implements OnInit, OnChanges, AfterViewInit {
  @ViewChild(GoogleMap, { static: false }) map: GoogleMap;
  @ViewChildren(MapInfoWindow) infoWindowsView: QueryList<MapInfoWindow>;

  @Input() public trafficList: Device[];
  @Input() public passedHeight: number = null;
  @Input() private initialZoom: number;
  @Input() public isDarkActive: boolean;

  private readonly mainService = inject(MainSubscriptionsService);
  private readonly passDataService = inject(PassDataService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  private listLocations: Location[];
  public mapOptions: google.maps.MapOptions;
  public mapBounds: google.maps.LatLngBounds;
  public mapZoom: number;
  private defaultPosition: google.maps.LatLng = null;
  public markers: Array<GoogleMarker> = [];
  public isAdmin: boolean = false;
  public loadingNavigation: boolean = false;
  public refreshMap: boolean = false;
  private iconsForMap: Icon[] = [];
  private readonly guidPattern = new RegExp('^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$', 'i');

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isDarkActive && changes.isDarkActive.currentValue !== changes.isDarkActive.previousValue && !changes.isDarkActive.firstChange) {
      this.mapZoom = this.map.getZoom();
      this.mapBounds = this.map.getBounds();

      this.refreshMap = true;
      timer(0).subscribe(() => {
        this.mapOptions = {
          mapId: this.isDarkActive ? 'af8729d51fc92cc9' : '805d80ad475b1388',
          gestureHandling: "cooperative",
          mapTypeId: "roadmap",
          zoom: this.mapZoom
        } as google.maps.MapOptions;

        this.refreshMap = false;
        this.setBounds();
      });
    }
  }

  ngOnInit(): void {
    this.passDataService.locations$.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(locations => {
        this.listLocations = locations;
      });

    this.passDataService.currentDomainProperties$.pipe(first()).subscribe(properties => {
      if (!properties?.length) {
        return;
      }

      let defCoordinatesProp: DomainProperty = properties
        .find((property: DomainProperty) => property.Key === 'DefaultCoordinates');
      if (defCoordinatesProp) {
        try {
          let coordinatesObject: any = JSON.parse(defCoordinatesProp.Value);
          if (['lat', 'lng'].every((key: string) => Object.keys(coordinatesObject).includes(key))) {
            this.defaultPosition = new google.maps.LatLng(coordinatesObject.lat, coordinatesObject.lng);
          }
        }
        catch {}
      }
    });

    this.passDataService.icons$.pipe(first()).subscribe(icons => {
      this.iconsForMap = icons;
    });

    this.mapOptions = {
      mapId: this.isDarkActive ? 'af8729d51fc92cc9' : '805d80ad475b1388',
      gestureHandling: "cooperative",
      mapTypeId: "roadmap"
    } as google.maps.MapOptions;
    
    if (this.trafficList?.length > 0) {
      for (let i = 0; i < this.trafficList.length; i++) {
          let children = this.buildDevices(this.trafficList[i].Childs);
          let deviceType: DeviceType = this.trafficList[i].Model.Type;
          this.markers.push({
            position: new google.maps.LatLng(this.trafficList[i].Latitude, this.trafficList[i].Longitude),
            label: {
              color: 'black',
              text: this.trafficList[i].Name.length > 12 ? this.trafficList[i].Name.slice(0, 12) + '...' : this.trafficList[i].Name,
              fontSize: '0px',
              fontWeight: 'normal',
              scaledSize: new google.maps.Size(80, 80),
              origin: new google.maps.Point(0, 0),
              anchor: new google.maps.Point(20, 0),
              labelOrigin: null
            },
            title: this.trafficList[i].Name,
            icon: this.getIcon(deviceType.MapPin, '#' + deviceType.Color),
            info: this.trafficList[i].Id,
            childs: this.buildInfoContentArray(children)
          });

      }
    }

    if (this.listLocations?.length > 0) {
      for (let i = 0; i < this.listLocations.length; i++) {

        this.markers.push({
          position: new google.maps.LatLng(+this.listLocations[i].Latitude, +this.listLocations[i].Longitude),
          label: {
            color: 'black',
            text: this.listLocations[i].Name.length > 12 ? this.listLocations[i].Name.slice(0, 12) + '...' : this.listLocations[i].Name,
            fontSize: '0px',
            fontWeight: 'normal',
            scaledSize: new google.maps.Size(80, 80),
            origin: new google.maps.Point(0, 0),
            anchor: new google.maps.Point(20, 0),
            labelOrigin: null
          },
          title: this.listLocations[i].Name,
          icon: this.getIcon('pin-location'),
          info: 'Location:' + this.listLocations[i].Id
        });

      }
    }
  }

  ngAfterViewInit() {
    this.setBounds();
  }

  private setBounds(): void {
    if (this.mapBounds === undefined) {
      this.mapBounds = new google.maps.LatLngBounds();

      if (this.trafficList?.length > 0) {
        for (let i = 0; i < this.trafficList.length; i++) {
          this.mapBounds.extend(new google.maps.LatLng(this.trafficList[i].Latitude, this.trafficList[i].Longitude));
        }
        timer(0).subscribe(() => {
          this.map.fitBounds(this.mapBounds);
        });
      }
      else if (this.defaultPosition) {
        this.mapBounds.extend(this.defaultPosition);
        this.map.fitBounds(this.mapBounds);
        this.map.boundsChanged.pipe(first()).subscribe(() => {
          if (this.initialZoom !== undefined) {
            this.map.googleMap.setZoom(this.initialZoom);
          }
        });
      }
    } else {
      timer(0).subscribe(() => {
        this.map.fitBounds(this.mapBounds, 0);
      });
    }
  }

  public openInfoWindow(marker: MapAdvancedMarker, windowIndex: number, info: string): void {
    if (!info.startsWith('Location:')) {
      this.infoWindowsView?.get(windowIndex)?.open(marker);
    }
  }

  public openInfo(content: string): void {
    if (content && content != '' && content.match(this.guidPattern)) {
      this.goToDeviceDetail(content);
    }
  }

  private buildInfoContentArray(devices: Device[]): InfoContentData[] {

    if (!devices || devices === undefined) {
      return [];
    }

    let elements: Array<InfoContentData> = [];

    devices.forEach((element) => {
      if (elements.some(function (item) {
        return item.Name == element.Model.Type.Name
      })) {
        elements.find(x => x.Name == element.Model.Type.Name).Elements++;
      }
      else {
        elements.push(new InfoContentData(element.Model.Type.Name, element.Model.Type.Icon))
      }
    });

    return elements;
  }

  private buildDevices(devices: Device[]): Device[] {
    if (!devices || devices === undefined) {
      return [];
    }

    let childs: Device[] = [];

    for (let device of devices) {
      if (device.Childs?.length > 0) {
        childs.push(device);
        for (let child of device.Childs) {
          childs.push(child);
        }
      }
      else {
        childs.push(device);
      }
    }

    return childs;
  }
  
  private getIcon(deviceTypeMapPin: string, pinColor?: string): HTMLElement {
    if (deviceTypeMapPin?.includes('_')) {
      deviceTypeMapPin.replace('_', '-')
    }

    let iconContent: string = this.iconsForMap?.find(icon => icon.Name === deviceTypeMapPin)?.Content;
    if (!iconContent) {
      return null;
    }

    if (!pinColor) {
      pinColor = window.getComputedStyle(document.body).getPropertyValue('--custom-chart-green-color');
      if (!pinColor || pinColor === '') {
        pinColor = '#69d27d';
      }
    } 
    iconContent = iconContent.replace(/\$color/g, pinColor);

    return new DOMParser().parseFromString(iconContent, 'image/svg+xml').documentElement;
  }

  public goToDeviceDetail(deviceId: string): void {
    if(!this.loadingNavigation) {
      this.loadingNavigation = true;
      let currentUrl: string = this.router.url.split('/').pop().split(';')[0];

      this.mainService.setNavigationInfoComand({ Id: deviceId, BackRoute: currentUrl });
      this.mainService.updateAllDataAndNavigateComand('device');
    }
  }
}
